import React, { useCallback, useEffect, useMemo, useState } from 'react';

import useSittingStore from '../stores/sittingStore';
import useResumeVisit from './useResumeVisit';
import {
  listPaymentsForSitting,
  openProcedureReceiptPdf,
  excludeOptionalStep,
} from '../services/api/patientPlanApi';
import { openReceiptForCollectResult } from '../services/receiptHelper';
import { ROUTES } from '../constants/routes';
import ReassignDialog from '../components/procedures/ReassignDialog';
import ApprovalReviewDialog from '../components/procedures/ApprovalReviewDialog';
import ProcedureNoteDialog from '../components/procedures/ProcedureNoteDialog';
import VisitAssignmentsSlip from '../components/procedures/VisitAssignmentsSlip';
import ProcedureStartConfirmDialog from '../components/procedures/widget/ProcedureStartConfirmDialog';
import ResumeConflictDialog from '../components/dashboard/ResumeConflictDialog';
import { Z_DIALOG } from '../constants/zLayers';

// Phase 4.0j — sessionStorage suppression for the start-confirmation
// dialog. Once a sitting has been confirmed, skip the dialog for 60s so
// the assignee doesn't get banner-blind from repeated taps within the
// same active block.
const START_CONFIRM_TTL_MS = 60_000;
const START_CONFIRM_KEY = 'proc:start-confirmed-at';

function readStartConfirmMap() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage?.getItem(START_CONFIRM_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_e) {
    return {};
  }
}

function writeStartConfirmMap(map) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage?.setItem(START_CONFIRM_KEY, JSON.stringify(map));
  } catch (_e) {
    /* ignore */
  }
}

function wasRecentlyConfirmed(sittingId) {
  if (!sittingId) return false;
  const map = readStartConfirmMap();
  const at = map[sittingId];
  return typeof at === 'number' && Date.now() - at < START_CONFIRM_TTL_MS;
}

function markRecentlyConfirmed(sittingId) {
  if (!sittingId) return;
  const map = readStartConfirmMap();
  map[sittingId] = Date.now();
  // Compact: drop entries older than the TTL so the blob doesn't grow
  // forever across a long shift.
  const now = Date.now();
  for (const [k, v] of Object.entries(map)) {
    if (typeof v !== 'number' || now - v >= START_CONFIRM_TTL_MS) {
      delete map[k];
    }
  }
  writeStartConfirmMap(map);
}

/**
 * useSittingActions — shared orchestration for every surface that renders
 * SittingCard (ProcedureQueuePage + ActiveProceduresBoard). Owns the
 * dialog flow for actions that need user input (collect_payment / reject
 * / cancel / reassign / review) so both surfaces behave identically.
 *
 * Returns:
 *   handleAction — async ({ action, sitting }) => void (SittingCard onAction)
 *   dialogs     — ReactNode to mount once in the page root
 */
export default function useSittingActions({ viewer, onAfterAction, navigate }) {
  const {
    collectPayment,
    executeSitting,
    completeSitting,
    approveSitting,
    rejectSitting,
    cancelSitting,
    assignSitting,
  } = useSittingStore();

  // A2-S6 — the assignee list for reassignment is now sourced by
  // ReassignDialog itself from the dept-scoped assignable-staff endpoint
  // (server-side role ∩ dept). We no longer prefetch the unscoped org roster
  // here, so the picker can never surface out-of-dept / role-ineligible staff.

  const [prompt, setPrompt] = useState(null);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [noteTarget, setNoteTarget] = useState(null);
  const [slipTarget, setSlipTarget] = useState(null); // Phase 1f — assignments slip
  const [startConfirmTarget, setStartConfirmTarget] = useState(null); // Phase 4.0j

  const fireReload = useCallback(() => {
    if (typeof onAfterAction === 'function') onAfterAction();
  }, [onAfterAction]);

  // Resume-visit orchestration (conflict-check, optional park, backend
  // call). Used both when the chip's primary CTA is "Resume Visit"
  // (visit_status === 'COMPLETED') and as a defensive guard in the
  // execute path — so a stale UI state can never sneak a Start through
  // on a closed visit.
  const { requestResume, dialogProps: resumeDialogProps } = useResumeVisit({
    viewerMemberId: viewer?.memberId,
  });

  const navigateToVisit = useCallback(
    (visitId) => {
      if (!visitId || typeof navigate !== 'function') return;
      navigate(`${ROUTES.ORG}#visit=${visitId}`);
    },
    [navigate],
  );

  const handleReceiptAction = useCallback(async (sitting) => {
    const { plan_id: planId, sitting_id: sittingId } = sitting;
    const listResult = await listPaymentsForSitting(planId, sittingId);
    if (!listResult?.success) return;
    const payments = listResult.data?.payments || [];
    if (payments.length === 0) return;
    const latest = payments[payments.length - 1];
    await openProcedureReceiptPdf(latest.payment_id);
  }, []);

  const handleAction = useCallback(
    async ({ action, sitting, skipConfirm = false }) => {
      const { plan_id: planId, sitting_id: sittingId } = sitting;
      // NOTE: only actions that mutate immediately call fireReload(). Actions
      // that merely OPEN a dialog (reassign/cancel/reject/review/write_note/
      // collect_payment/...) must NOT reload here — the dialog's submit
      // handler reloads after the real change. Reloading on open refetched
      // the queue under the just-clicked chip and made the list flash/reorder
      // (the "glitch"), and could tear the freshly-opened dialog down.
      switch (action) {
        case 'collect_payment':
          setPrompt({ action, sitting });
          return;
        case 'resume_visit': {
          // Resume Visit chip CTA — shown in place of Start when the
          // parent visit is COMPLETED + same-day. Re-opens the visit
          // (with optional park-current via dialog) and navigates to
          // the visit screen so the doctor regains the full widget
          // surface (procedure add, dental chart, co-worker picker).
          // We do NOT auto-execute the sitting afterward; the chip
          // reverts to Start naturally once the visit flips to
          // IN_PROGRESS and the user can choose what to do next.
          const targetVisit = {
            visit_id: sitting.visit_id,
            patient_name: sitting.patient_name,
            doctor_member_id: sitting.visit_doctor_member_id,
          };
          try {
            await requestResume(targetVisit, {
              afterResume: () => navigateToVisit(sitting.visit_id),
            });
          } catch (err) {
            console.warn('[useSittingActions] resume_visit failed:', err);
            // Never leave the click unexplained — surface a plain-language
            // reason instead of silently swallowing the error.
            window.alert(
              'Couldn’t resume this visit. It may have already been completed '
              + 'or picked up by someone else — refresh and try again, or start '
              + 'a new visit for this patient.',
            );
          }
          return;
        }
        case 'execute':
          // Defensive: if the chip somehow dispatched 'execute' on a
          // sitting whose parent visit is COMPLETED (stale UI state,
          // pre-projection sitting cache), reroute through resume
          // before the start can hit the server. Server would 409 on
          // a status-mismatch anyway, but this keeps the UX coherent.
          if ((sitting.visit_status || '').toUpperCase() === 'COMPLETED') {
            const targetVisit = {
              visit_id: sitting.visit_id,
              patient_name: sitting.patient_name,
              doctor_member_id: sitting.visit_doctor_member_id,
            };
            try {
              await requestResume(targetVisit, {
                afterResume: () => navigateToVisit(sitting.visit_id),
              });
            } catch (err) {
              console.warn(
                '[useSittingActions] execute-on-COMPLETED resume failed:',
                err,
              );
            }
            return;
          }
          // Phase 4.0j — patient-identity guard. Skip when the
          // assignee has already confirmed this sitting in the last
          // 60s (avoids modal fatigue on rapid retries).
          // Phase 5 — also skip when the caller has already narrowed
          // the panel to a single visit via a queue filter; the
          // patient is already disambiguated by the filter so the
          // confirm modal becomes redundant.
          if (skipConfirm || wasRecentlyConfirmed(sittingId)) {
            await executeSitting(planId, sittingId);
            fireReload();  // immediate mutation — refresh the queue
            return;
          }
          setStartConfirmTarget(sitting);
          return;
        case 'complete':
          await completeSitting(planId, sittingId, {});
          fireReload();  // immediate mutation
          return;
        case 'approve':
          await approveSitting(planId, sittingId);
          fireReload();  // immediate mutation
          return;
        // Dialog-opening actions: do NOT reload here. The dialog's submit
        // handler (submitPrompt / submitReassign / review onApprove…)
        // reloads after the real change lands.
        case 'reject':
          setPrompt({ action, sitting });
          return;
        case 'cancel':
          setPrompt({ action, sitting });
          return;
        case 'reassign':
          setReassignTarget(sitting);
          return;
        case 'review':
          setReviewTarget(sitting);
          return;
        case 'write_note':
          setNoteTarget(sitting);
          return;
        case 'print_assignments':
          // Phase 1f — receptionist / admin print of "who's doing
          // what on this visit." Uses byVisit projection so we have
          // the full set of sittings + assignees without a fetch.
          setSlipTarget(sitting);
          return;
        case 'open_visit':
          if (sitting.visit_id && navigate) {
            navigate(`${ROUTES.ORG}#visit=${sitting.visit_id}`);
          }
          return;
        case 'receipt':
          await handleReceiptAction(sitting);
          return;
        case 'exclude_from_plan': {
          // Phase 2.7 — reception/doctor opts the patient out of an
          // optional workflow step. Confirm because cancellation cascades.
          if (!sitting.source_step_id) return;
          const ok = window.confirm(
            `Exclude this optional step from the plan? `
            + `The sitting will be cancelled and the plan progresses without it.`,
          );
          if (!ok) return;
          const r = await excludeOptionalStep(
            sitting.plan_id, sitting.source_step_id,
          );
          if (!r.success) {
            window.alert(r.error || 'Could not exclude step.');
          } else {
            fireReload();  // immediate mutation
          }
          return;
        }
        default:
          break;
      }
    },
    [
      executeSitting, completeSitting, approveSitting, fireReload, navigate,
      handleReceiptAction, requestResume, navigateToVisit,
    ],
  );

  const submitStartConfirm = async () => {
    if (!startConfirmTarget) return;
    const { plan_id: planId, sitting_id: sittingId } = startConfirmTarget;
    markRecentlyConfirmed(sittingId);
    // eslint-disable-next-line no-console
    console.info(`metric=procedure_start_confirmed sitting=${sittingId}`);
    try {
      await executeSitting(planId, sittingId);
    } finally {
      setStartConfirmTarget(null);
      fireReload();
    }
  };

  const cancelStartConfirm = () => {
    if (startConfirmTarget?.sitting_id) {
      // eslint-disable-next-line no-console
      console.info(
        `metric=procedure_start_aborted sitting=${startConfirmTarget.sitting_id}`,
      );
    }
    setStartConfirmTarget(null);
  };

  const submitReassign = async ({ assigned_to_member_id, reason }) => {
    if (!reassignTarget) return;
    const { plan_id: planId, sitting_id: sittingId } = reassignTarget;
    const result = await assignSitting(planId, sittingId, {
      assigned_to_member_id,
      reason,
    });
    if (result?.success) {
      setReassignTarget(null);
      fireReload();
    }
    return result;
  };

  const submitPrompt = async (values) => {
    if (!prompt) return;
    const { action, sitting } = prompt;
    const { plan_id: planId, sitting_id: sittingId } = sitting;
    let result;
    if (action === 'collect_payment') {
      result = await collectPayment(planId, sittingId, {
        amount: values.amount,
        payment_mode: values.payment_mode,
      });
      // Phase 1f — A5 PDF invoice is the sole receipt for procedure
      // payments. Shared helper opens it + surfaces failures (single
      // source of truth across all collect surfaces).
      await openReceiptForCollectResult(result);
    } else if (action === 'reject') {
      result = await rejectSitting(planId, sittingId, { rejection_reason: values.reason });
    } else if (action === 'cancel') {
      result = await cancelSitting(planId, sittingId, { reason: values.reason });
    }
    if (result?.success) {
      setPrompt(null);
      fireReload();
    }
  };

  const dialogs = (
    <>
      {prompt && (
        <PromptDialog
          action={prompt.action}
          sitting={prompt.sitting}
          onSubmit={submitPrompt}
          onCancel={() => setPrompt(null)}
        />
      )}
      {reassignTarget && (
        <ReassignDialog
          sitting={reassignTarget}
          viewer={viewer}
          onSubmit={submitReassign}
          onCancel={() => setReassignTarget(null)}
        />
      )}
      {reviewTarget && (
        <ApprovalReviewDialog
          planId={reviewTarget.plan_id}
          sittingId={reviewTarget.sitting_id}
          onApprove={async () => {
            await approveSitting(reviewTarget.plan_id, reviewTarget.sitting_id);
            setReviewTarget(null);
            fireReload();
          }}
          onReject={async ({ rejection_reason }) => {
            await rejectSitting(
              reviewTarget.plan_id, reviewTarget.sitting_id,
              { rejection_reason },
            );
            setReviewTarget(null);
            fireReload();
          }}
          onClose={() => setReviewTarget(null)}
        />
      )}
      {noteTarget && (
        <ProcedureNoteDialog
          sitting={noteTarget}
          viewer={viewer}
          onClose={() => setNoteTarget(null)}
          onSaved={fireReload}
        />
      )}
      {slipTarget && (
        <AssignmentsSlipContainer
          sitting={slipTarget}
          onClose={() => setSlipTarget(null)}
        />
      )}
      {startConfirmTarget && (
        <ProcedureStartConfirmDialog
          sitting={startConfirmTarget}
          onConfirm={submitStartConfirm}
          onCancel={cancelStartConfirm}
        />
      )}
      <ResumeConflictDialog {...resumeDialogProps} />
    </>
  );

  return { handleAction, dialogs };
}

function PromptDialog({ action, sitting, onSubmit, onCancel }) {
  const [amount, setAmount] = useState(sitting.final_cost?.toString() || '');
  // No payment-mode default — staff must pick UPI / CASH / CARD.
  const [mode, setMode] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');

  const isPayment = action === 'collect_payment';
  const isReject = action === 'reject';
  const isCancel = action === 'cancel';

  const title = isPayment ? 'Collect payment' : isReject ? 'Reject sitting' : 'Cancel sitting';

  const submit = async (e) => {
    e.preventDefault();
    if (isPayment && !mode) {
      setLocalError('Please select a payment method.');
      return;
    }
    setLocalError('');
    setBusy(true);
    try {
      if (isPayment) {
        await onSubmit({ amount, payment_mode: mode });
      } else {
        await onSubmit({ reason: reason.trim() });
      }
    } finally {
      setBusy(false);
    }
  };

  const reasonValid = !isPayment ? reason.trim().length >= 3 : true;
  const paymentValid = isPayment ? !!mode : true;

  return (
    <div
      // Phase 4 — z-50 was hidden behind the chart pane (Z_CHART_PANE=900).
      // Z_DIALOG (1300) keeps the collect/reject/cancel modal above the
      // chart pane, widget rail, and any active widget overlay, but below
      // toasts. Same fix pattern as BundledPaymentSheet / VisitReceiptsModal.
      // role="dialog" marks this as a keep-zone so RoleTaskBadge doesn't
      // collapse (and unmount this modal) when the user clicks inside it.
      role="dialog"
      aria-modal="true"
      style={{ zIndex: Z_DIALOG }}
      className="fixed inset-0 flex items-center justify-center bg-black/40 p-4"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5"
      >
        <h2 className="text-lg font-semibold mb-3">{title}</h2>

        {isPayment && (
          <>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Amount (₹)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                min="0"
                step="1"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Mode</label>
              <div className="flex gap-2">
                {['UPI', 'CASH', 'CARD'].map((m) => (
                  <button
                    type="button"
                    key={m}
                    onClick={() => { setMode(m); setLocalError(''); }}
                    className={`px-3 py-1.5 rounded-full text-sm border ${
                      mode === m ? 'bg-teal-600 text-white border-teal-600' : 'border-slate-300'
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              {localError && (
                <p className="mt-1 text-xs text-rose-600 dark:text-rose-300">
                  {localError}
                </p>
              )}
            </div>
          </>
        )}

        {(isReject || isCancel) && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Reason (min 3 chars)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              required
              minLength={3}
            />
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !reasonValid || !paymentValid}
            className="px-4 py-1.5 text-sm rounded-md bg-teal-600 hover:bg-teal-700 text-white disabled:bg-teal-300"
          >
            {busy ? 'Submitting…' : 'Confirm'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Phase 1f — container that reads `byVisit` from the store and renders
// the slip. Kept in this file to stay colocated with the other dialog
// controllers and to inherit the `useSittingStore` subscription.
function AssignmentsSlipContainer({ sitting, onClose }) {
  const byVisit = useSittingStore((s) => s.byVisit);
  const fetchForVisit = useSittingStore((s) => s.fetchForVisit);
  const visitId = sitting?.visit_id;
  const visitSittings = useMemo(
    () => (visitId ? (byVisit?.[visitId] || []) : []),
    [byVisit, visitId],
  );

  useEffect(() => {
    if (visitId && (!byVisit?.[visitId] || byVisit[visitId].length === 0)) {
      fetchForVisit(visitId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId]);

  // If the byVisit list is empty (not fetched yet), fall back to the
  // single sitting so the slip isn't blank.
  const list = visitSittings.length > 0 ? visitSittings : [sitting];

  // Phase 1f observability — one-line print counter.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.info('metric=print_assignments_invocations value=1 visit=%s', visitId);
  }, [visitId]);

  const visitMeta = {
    visit_id: visitId,
    patient_name: sitting?.patient_name || list[0]?.patient_name,
    patient_mobile: sitting?.patient_mobile,
    patient_dob: sitting?.patient_dob || sitting?.dob,
    queue_number: sitting?.queue_number ?? list[0]?.queue_number,
    doctor_name: sitting?.visit_doctor_name || sitting?.doctor_name,
    visit_date: sitting?.scheduled_for?.slice(0, 10)
      || new Date().toISOString().slice(0, 10),
  };

  return (
    <VisitAssignmentsSlip
      visit={visitMeta}
      sittings={list}
      onClose={onClose}
    />
  );
}
